<template>
  <div class="character-page">
    <div class="page-header">
      <div class="header-copy">
        <div class="title-row">
          <h1 class="page-title">设计角色</h1>
          <span class="total-badge">{{ characterCount }}个角色</span>
        </div>
        <p class="page-description">管理绘本故事主角形象, 让AI创作独一无二的故事</p>
      </div>

      <el-button class="create-button" type="primary" :icon="MagicStick" @click="requestCreate">
        创建角色
      </el-button>
    </div>

    <div v-loading="loading" class="content-area" @scroll="handleListScroll">
      <div class="characters-grid">
        <AddCharacterCard @create="requestCreate" />

        <CharacterCard
          v-for="char in characters"
          :key="char.id"
          :character="char"
          :summary="characterSummary(char)"
          :fallback-color="colorOf(char.id)"
          @edit="openEdit(char)"
          @delete="handleDelete(char)"
          @start="startCreation(char)"
        />
      </div>
    </div>

    <MobileCreateBar @create="requestCreate" />

    <PhoneBindingGuard ref="phoneBindingGuardRef" />

    <CharacterEditorDialog
      v-model:visible="dialogVisible"
      v-model:appearance-tab="appearanceTab"
      :form="form"
      :is-edit="isEdit"
      :saving="saving"
      :generating="generating"
      :generation-step="generationStep"
      :can-generate="canGenerate"
      :can-confirm-generation="canConfirmGeneration"
      :show-membership-upgrade="showMembershipUpgrade"
      :generation-cost="characterGenerationCost"
      @save="handleSave"
      @redescribe="handleRedescribeCharacter"
      @confirm="handleConfirmGeneration"
      @upgrade="handleUpgradeMembership"
      @closed="resetForm"
    />

    <InsufficientCoinsModal
      v-model="insufficientCoinsModalVisible"
      action-text="生成角色"
      :required-coins="characterGenerationCost"
      :current-coins="balanceStore.balance"
      :member-coins="premiumCharacterGenerationCost"
      @recharge="handleBuyCoin"
      @upgrade="handleUpgradeMembership"
      @closed="handleInsufficientCoinsClosed"
    />

    <Teleport to="body">
      <Transition name="character-delete-dialog">
        <div
          v-if="deleteDialogVisible"
          class="delete-dialog-overlay"
          role="presentation"
          @click.self="closeDeleteDialog"
        >
          <section
            class="delete-dialog-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="character-delete-dialog-title"
          >
            <div class="delete-dialog-header">
              <h2 id="character-delete-dialog-title" class="delete-dialog-title">提示</h2>
              <button
                class="delete-dialog-close"
                type="button"
                aria-label="关闭提示"
                :disabled="deleting"
                @click="closeDeleteDialog"
              >
                <SvgIcon name="close-line" :size="11" />
              </button>
            </div>

            <div class="delete-dialog-body">
              <p class="delete-dialog-description">
                您确定删除该角色吗？删除后创作绘本时将无法引用该角色
              </p>
            </div>

            <div class="delete-dialog-actions">
              <el-button
                class="delete-dialog-cancel"
                :disabled="deleting"
                @click="closeDeleteDialog"
              >
                取消
              </el-button>
              <el-button
                class="delete-dialog-confirm"
                type="primary"
                :loading="deleting"
                @click="confirmDelete"
              >
                确定
              </el-button>
            </div>
          </section>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
  import { computed, onActivated, onBeforeUnmount, onDeactivated, reactive, ref } from 'vue';
  import { useRouter } from 'vue-router';
  import { ElMessage } from 'element-plus';
  import { MagicStick } from '@element-plus/icons-vue';
  import AddCharacterCard from './components/AddCharacterCard.vue';
  import CharacterCard from './components/CharacterCard.vue';
  import CharacterEditorDialog from './components/CharacterEditorDialog.vue';
  import MobileCreateBar from './components/MobileCreateBar.vue';
  import InsufficientCoinsModal from '@/components/common/InsufficientCoinsModal.vue';
  import PhoneBindingGuard from '@/components/common/PhoneBindingGuard.vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import { useCoinGift, useUpgradeModal } from '@/hooks';
  import {
    CHARACTER_PAGE_SIZE,
    createCharacter,
    deleteCharacter,
    fetchCharacterList,
    getCharacterTaskProgress,
    resolveCharacterTaskResult,
    waitForCharacterTask,
    updateCharacter
  } from '@/api/character';
  import type { CharacterItem } from '@/api/character';
  import { CHARACTER_DEFAULT_ART_STYLE } from '@/constants/characterArtStyles';
  import {
    IMAGE_GENERATION_PRICE_BY_LEVEL,
    normalizeImageGenerationPriceLevel
  } from '@/constants/imageGenerationPricing';
  import { useBalanceStore, useUserStore } from '@/store';

  const router = useRouter();
  const balanceStore = useBalanceStore();
  const userStore = useUserStore();
  const { showUpgrade: handleUpgradeMembership, showCoinBuy: handleBuyCoin } = useUpgradeModal();
  const { checkAndOpenCoinGift } = useCoinGift();

  const handleInsufficientCoinsClosed = (reason: 'cancel' | 'recharge' | 'upgrade') => {
    if (reason === 'cancel') void checkAndOpenCoinGift();
  };

  const premiumCharacterGenerationCost = IMAGE_GENERATION_PRICE_BY_LEVEL[2].character;

  const COLORS = [
    'linear-gradient(135deg,#a78bfa,#7c3aed)',
    'linear-gradient(135deg,#fb7185,#e11d48)',
    'linear-gradient(135deg,#34d399,#059669)',
    'linear-gradient(135deg,#60a5fa,#2563eb)',
    'linear-gradient(135deg,#fbbf24,#d97706)'
  ];
  const CHARACTER_TASK_POLL_INTERVAL_MS = 3000;

  const characters = ref<CharacterItem[]>([]);
  const loading = ref(false);
  const currentPage = ref(0);
  const totalCount = ref(0);
  const hasMore = ref(true);
  const dialogVisible = ref(false);
  const isEdit = ref(false);
  const saving = ref(false);
  const generating = ref(false);
  const generationStep = ref<'form' | 'preview'>('form');
  const generationRequestId = ref(0);
  const appearanceTab = ref<'text' | 'image'>('image');
  const pendingCreatedCharacterId = ref('');
  const editingCharacterId = ref('');
  const insufficientCoinsModalVisible = ref(false);
  const phoneBindingGuardRef = ref<InstanceType<typeof PhoneBindingGuard>>();
  const deleteDialogVisible = ref(false);
  const deleting = ref(false);
  const pendingDeleteCharacter = ref<CharacterItem | null>(null);
  let latestListRequestId = 0;
  let characterTaskPollingTimer: number | undefined;
  let characterTaskPollingVersion = 0;
  let isCharacterPageActive = false;
  let createRequestPending = false;

  const imagePriceLevel = computed(() =>
    normalizeImageGenerationPriceLevel(userStore.userData.uType)
  );
  const characterGenerationCost = computed(
    () => IMAGE_GENERATION_PRICE_BY_LEVEL[imagePriceLevel.value].character
  );
  const showMembershipUpgrade = computed(
    () => userStore.isLogin && (imagePriceLevel.value === 0 || imagePriceLevel.value === 1)
  );

  const emptyForm = (): Omit<CharacterItem, 'id' | 'createdAt' | 'updatedAt'> => ({
    name: '',
    age: undefined,
    gender: '',
    personality: [],
    catchphrase: '',
    artStyle: CHARACTER_DEFAULT_ART_STYLE,
    avatarUrl: '',
    generatedUrl: '',
    refImageUrl: '',
    description: '',
    setting: ''
  });

  const form = reactive<ReturnType<typeof emptyForm> & { id?: string }>(emptyForm());

  const hashString = (value: string) =>
    Array.from(value).reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) >>> 0, 0);
  const colorOf = (id: string) => COLORS[hashString(id) % COLORS.length];
  const canGenerate = computed(() => {
    const hasAppearance =
      appearanceTab.value === 'text' ? form.description.trim() : Boolean(form.refImageUrl);

    return Boolean(form.artStyle && hasAppearance);
  });
  const canConfirmGeneration = computed(() =>
    Boolean(form.id && form.name.trim() && form.setting.trim())
  );

  const characterSummary = (char: CharacterItem) => {
    return char.setting.trim();
  };
  const characterCount = computed(() => totalCount.value || characters.value.length);

  const isGeneratingCharacter = (char: CharacterItem) =>
    char.status === 'queued' || char.status === 'running' || char.status === 'processing';

  const canPollCharacterTask = (char: CharacterItem) =>
    isGeneratingCharacter(char) && !!char.taskId;

  const isCurrentCharacterTaskPolling = (pollingVersion: number) =>
    isCharacterPageActive && pollingVersion === characterTaskPollingVersion;

  const withThumbnailRefresh = (source: string, refreshVersion: number) => {
    const url = source.trim();
    if (!url) return '';

    const hashIndex = url.indexOf('#');
    const baseUrl = hashIndex === -1 ? url : url.slice(0, hashIndex);
    const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
    const separator = baseUrl.includes('?') ? '&' : '?';

    return `${baseUrl}${separator}_thumbnailRefresh=${refreshVersion}${hash}`;
  };

  const updateCharacterInList = (
    characterId: string,
    updater: (character: CharacterItem) => CharacterItem
  ) => {
    const index = characters.value.findIndex((char) => char.id === characterId);
    if (index === -1) return;

    characters.value.splice(index, 1, updater(characters.value[index]));
  };

  const refreshCompletedCharacter = (character: CharacterItem, pollingVersion: number) => {
    if (!isCurrentCharacterTaskPolling(pollingVersion)) return;

    updateCharacterInList(character.id, (current) => {
      if (character.taskId && current.taskId && current.taskId !== character.taskId) return current;

      const avatarUrl = current.avatarUrl.trim();
      const refreshVersion = Date.now();
      const nextCharacter: CharacterItem = {
        ...current,
        status: 'completed',
        taskId: ''
      };

      if (avatarUrl) nextCharacter.avatarUrl = withThumbnailRefresh(avatarUrl, refreshVersion);

      return nextCharacter;
    });
  };

  const queryCharacterTask = async (character: CharacterItem, pollingVersion: number) => {
    const taskId = character.taskId;
    if (!taskId) return;

    try {
      const progress = await getCharacterTaskProgress(taskId);
      if (!isCurrentCharacterTaskPolling(pollingVersion)) return;

      if (progress.status === 'succeeded') {
        refreshCompletedCharacter(character, pollingVersion);
        return;
      }

      if (progress.status === 'failed') {
        updateCharacterInList(character.id, (current) =>
          current.taskId === taskId ? { ...current, status: 'failed' } : current
        );
        return;
      }

      const nextStatus = progress.status === 'running' ? 'running' : 'queued';
      updateCharacterInList(character.id, (current) =>
        current.taskId === taskId ? { ...current, status: nextStatus } : current
      );
    } catch (error) {
      if (isCurrentCharacterTaskPolling(pollingVersion)) {
        console.warn('[character-list] 查询角色生成进度失败，将重试:', error);
      }
    }
  };

  const pollCharacterTasks = async (pollingVersion: number) => {
    const pendingCharacters = characters.value.filter(canPollCharacterTask);
    if (!pendingCharacters.length) return;

    await Promise.all(
      pendingCharacters.map((character) => queryCharacterTask(character, pollingVersion))
    );

    if (!isCurrentCharacterTaskPolling(pollingVersion)) return;

    if (!characters.value.some(canPollCharacterTask)) return;

    characterTaskPollingTimer = window.setTimeout(() => {
      characterTaskPollingTimer = undefined;
      void pollCharacterTasks(pollingVersion);
    }, CHARACTER_TASK_POLL_INTERVAL_MS);
  };

  const stopCharacterTaskPolling = () => {
    characterTaskPollingVersion += 1;

    if (characterTaskPollingTimer !== undefined) {
      window.clearTimeout(characterTaskPollingTimer);
      characterTaskPollingTimer = undefined;
    }
  };

  const restartCharacterTaskPolling = () => {
    stopCharacterTaskPolling();

    if (!isCharacterPageActive || !characters.value.some(canPollCharacterTask)) return;

    void pollCharacterTasks(characterTaskPollingVersion);
  };

  const loadList = async ({ reset = false }: { reset?: boolean } = {}) => {
    if (!reset && (loading.value || !hasMore.value)) return;

    const requestId = latestListRequestId + 1;
    const page = reset ? 1 : currentPage.value + 1;
    latestListRequestId = requestId;
    loading.value = true;
    try {
      const result = await fetchCharacterList({
        page,
        pagesize: CHARACTER_PAGE_SIZE
      });

      if (requestId !== latestListRequestId) return;

      characters.value = reset ? result.list : [...characters.value, ...result.list];
      currentPage.value = result.page;
      totalCount.value = result.total;
      hasMore.value =
        result.total > 0
          ? characters.value.length < result.total
          : result.list.length >= result.pagesize;
      restartCharacterTaskPolling();
    } catch {
      if (requestId !== latestListRequestId) return;

      ElMessage.error('角色列表加载失败，请稍后重试');
    } finally {
      if (requestId === latestListRequestId) loading.value = false;
    }
  };

  const handleListScroll = (event: Event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;

    const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (distanceToBottom > 24 || loading.value || !hasMore.value) return;

    void loadList();
  };

  const resetForm = () => {
    pendingCreatedCharacterId.value = '';
    generationRequestId.value += 1;
    generationStep.value = 'form';
    generating.value = false;
    saving.value = false;
    Object.assign(form, emptyForm());
    delete form.id;
    editingCharacterId.value = '';
  };

  const openCreate = () => {
    isEdit.value = false;
    resetForm();
    form.name = '';
    appearanceTab.value = 'image';
    generationStep.value = 'form';
    dialogVisible.value = true;
  };

  const requestCreate = async () => {
    if (createRequestPending) return;

    const phoneBindingGuard = phoneBindingGuardRef.value;
    if (!phoneBindingGuard) return;

    createRequestPending = true;
    try {
      if (await phoneBindingGuard.ensurePhoneBound()) openCreate();
    } finally {
      createRequestPending = false;
    }
  };

  const openEdit = (char: CharacterItem) => {
    isEdit.value = true;
    editingCharacterId.value = char.id;
    Object.assign(form, {
      ...char,
      name: char.name || '未命名',
      avatarUrl: char.avatarUrl || char.generatedUrl,
      generatedUrl: char.generatedUrl || char.avatarUrl
    });
    appearanceTab.value = char.refImageUrl ? 'image' : 'text';
    generationRequestId.value += 1;
    generationStep.value = char.status === 'completed' ? 'preview' : 'form';
    generating.value = false;
    saving.value = false;
    dialogVisible.value = true;
  };

  const generateCurrentCharacter = async (showPreview: boolean) => {
    if (!canGenerate.value) return;

    const requestId = generationRequestId.value + 1;
    generationRequestId.value = requestId;
    const requestedName = form.name;
    const requestedSetting = form.setting;
    const isEditingCharacter = Boolean(editingCharacterId.value);
    const targetCharacterId = editingCharacterId.value || pendingCreatedCharacterId.value;

    if (showPreview) {
      generationStep.value = 'preview';
      form.generatedUrl = '';
      form.avatarUrl = '';
    }

    generating.value = true;
    try {
      const created = await createCharacter(
        {
          ...form,
          refImageUrl: appearanceTab.value === 'image' ? form.refImageUrl : ''
        },
        targetCharacterId || undefined
      );
      void balanceStore.refreshBalance();

      if (generationRequestId.value !== requestId) {
        return false;
      }

      const currentName = form.name;
      const currentSetting = form.setting;
      Object.assign(form, created);
      if (currentName !== requestedName) form.name = currentName;
      if (currentSetting !== requestedSetting) form.setting = currentSetting;
      if (!isEditingCharacter) pendingCreatedCharacterId.value = created.id;

      if (!created.taskId) throw new Error('角色生成任务缺少任务ID');
      const completed = resolveCharacterTaskResult(
        await waitForCharacterTask(created.taskId),
        created
      );
      if (generationRequestId.value !== requestId) return false;

      const latestName = form.name;
      const latestSetting = form.setting;
      Object.assign(form, completed);
      form.name = latestName;
      form.setting = latestSetting;
      return true;
    } catch (error) {
      if (generationRequestId.value === requestId) {
        ElMessage.error(
          isTextScanFailedError(error) ? '输入内容审核不合规' : '角色生成失败，请稍后重试'
        );
        if (showPreview) generationStep.value = 'form';
      }
      return false;
    } finally {
      if (generationRequestId.value === requestId) generating.value = false;
    }
  };

  const confirmGeneratedCharacter = async () => {
    if (!form.id) throw new Error('缺少角色ID，无法保存角色');

    await updateCharacter(form.id, {
      name: form.name,
      setting: form.setting
    });
    pendingCreatedCharacterId.value = '';
    editingCharacterId.value = '';
    await loadList({ reset: true });
    ElMessage.success(isEdit.value ? '角色已更新' : '角色已创建');
    dialogVisible.value = false;
  };

  const getCharacterSaveErrorMessage = (error: unknown) => {
    if (typeof error === 'object' && error !== null) {
      const errorRecord = error as Record<string, unknown>;
      if (typeof errorRecord.msg === 'string' && errorRecord.msg.trim()) {
        return errorRecord.msg;
      }

      const response = errorRecord.response as Record<string, unknown> | undefined;
      const data = response?.data as Record<string, unknown> | undefined;
      if (typeof data?.msg === 'string' && data.msg.trim()) return data.msg;
    }

    return '角色保存失败，请稍后重试';
  };

  const isTextScanFailedError = (error: unknown) => {
    if (typeof error !== 'object' || error === null) return false;

    const errorRecord = error as Record<string, unknown>;
    if (errorRecord.code === 'TEXT_SCAN_FAILED') return true;

    const response = errorRecord.response as Record<string, unknown> | undefined;
    const data = response?.data as Record<string, unknown> | undefined;
    return data?.code === 'TEXT_SCAN_FAILED';
  };

  const handleSave = async () => {
    if (!canGenerate.value || generating.value || saving.value) return;

    saving.value = true;
    const balanceLoaded = await balanceStore.refreshBalance();
    saving.value = false;
    if (!isCharacterPageActive) return;

    if (balanceLoaded && balanceStore.balance < characterGenerationCost.value) {
      insufficientCoinsModalVisible.value = true;
      return;
    }

    void generateCurrentCharacter(true);
  };

  const handleRedescribeCharacter = () => {
    if (generating.value || saving.value) return;
    generationStep.value = 'form';
  };

  const handleConfirmGeneration = async () => {
    if (!canConfirmGeneration.value) return;

    saving.value = true;
    try {
      await confirmGeneratedCharacter();
    } catch (error) {
      ElMessage.error(getCharacterSaveErrorMessage(error));
    } finally {
      saving.value = false;
    }
  };

  const handleDelete = (char: CharacterItem) => {
    pendingDeleteCharacter.value = char;
    deleteDialogVisible.value = true;
  };

  const closeDeleteDialog = () => {
    if (deleting.value) return;

    deleteDialogVisible.value = false;
    pendingDeleteCharacter.value = null;
  };

  const confirmDelete = async () => {
    const char = pendingDeleteCharacter.value;
    if (!char || deleting.value) return;

    deleting.value = true;
    try {
      await deleteCharacter(char.id);
      characters.value = characters.value.filter((c) => c.id !== char.id);
      totalCount.value = Math.max(0, totalCount.value - 1);
      restartCharacterTaskPolling();
      deleteDialogVisible.value = false;
      pendingDeleteCharacter.value = null;
      ElMessage.success('已删除');
    } catch {
      ElMessage.error('删除失败，请稍后重试');
    } finally {
      deleting.value = false;
    }
  };

  const startCreation = (char: CharacterItem) => {
    void router.push({ name: 'create', query: { characterId: char.id } });
  };

  onActivated(() => {
    isCharacterPageActive = true;
    void loadList({ reset: true });
  });

  onDeactivated(() => {
    isCharacterPageActive = false;
    insufficientCoinsModalVisible.value = false;
    stopCharacterTaskPolling();
  });

  onBeforeUnmount(() => {
    isCharacterPageActive = false;
    stopCharacterTaskPolling();
  });
</script>

<style scoped>
  .character-page {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    overflow: hidden;
    padding: 23px 0 32px;
    background: #fbf9ff;
  }

  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
  }

  .header-copy {
    min-width: 0;
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: 23px;
    min-height: 40px;
  }

  .page-title {
    margin: 0;
    color: #160211;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 24px;
    font-weight: 700;
    line-height: 28px;
  }

  .total-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    min-width: 79px;
    box-sizing: border-box;
    padding: 0 16px;
    border-radius: 16px;
    background: #ede8ff;
    color: #6033e1;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: 19px;
  }

  .page-description {
    margin: 5px 0 0;
    color: #999;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }

  .create-button {
    width: 120px;
    height: 40px;
    border: 0;
    border-radius: 12px;
    background: #6033e1;
    color: #fff;
    box-shadow:
      0 10px 15px -3px rgba(99, 102, 241, 0.2),
      0 4px 6px -4px rgba(99, 102, 241, 0.2);
    font-size: 14px;
    font-weight: 500;
  }

  .create-button:hover,
  .create-button:focus {
    background: #6033e1;
    color: #fff;
  }

  .content-area {
    flex: 1 1 auto;
    margin-top: 35px;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    padding-bottom: 32px;
    box-sizing: border-box;
  }

  .characters-grid {
    --character-card-min-width: 236px;
    --character-card-max-width: 236px;

    display: flex;
    flex-wrap: wrap;
    gap: 39px;
    align-items: start;
  }

  .characters-grid > * {
    flex: 0 0 var(--character-card-max-width);
    min-width: 0;
    max-width: var(--character-card-max-width);
  }

  .delete-dialog-overlay {
    position: fixed;
    inset: 0;
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 24px;
    background: rgba(0, 0, 0, 0.45);
  }

  .delete-dialog-card {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 380px;
    min-height: 198px;
    box-sizing: border-box;
    padding: 20px;
    border-radius: 8px;
    background: #fff;
  }

  .delete-dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 340px;
    height: 26px;
  }

  .delete-dialog-title {
    margin: 0;
    color: #0f2f3e;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 23px;
  }

  .delete-dialog-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #0f2f3e;
    cursor: pointer;
  }

  .delete-dialog-close:hover,
  .delete-dialog-close:focus {
    background: #f5f3ff;
    color: #6033e1;
  }

  .delete-dialog-close:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  .delete-dialog-body {
    width: 340px;
    height: 60px;
    box-sizing: border-box;
    padding: 10px 20px;
  }

  .delete-dialog-description {
    width: 300px;
    margin: 0;
    color: #616569;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }

  .delete-dialog-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    width: 340px;
    height: 40px;
  }

  .delete-dialog-actions .el-button {
    width: 76px;
    height: 40px;
    margin: 0;
    padding: 0;
    border-radius: 6px;
    font-family: 'Microsoft YaHei', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
  }

  .delete-dialog-cancel.el-button {
    border-color: #e5e7eb;
    background: #fff;
    color: #333;
  }

  .delete-dialog-cancel.el-button:hover,
  .delete-dialog-cancel.el-button:focus {
    border-color: #d8d2ef;
    background: #fbf9ff;
    color: #6033e1;
  }

  .delete-dialog-confirm.el-button {
    border-color: #6033e1;
    background: #6033e1;
    color: #fff;
  }

  .delete-dialog-confirm.el-button:hover,
  .delete-dialog-confirm.el-button:focus {
    border-color: #6033e1;
    background: #6033e1;
    color: #fff;
  }

  .character-delete-dialog-enter-active,
  .character-delete-dialog-leave-active {
    transition: opacity 0.18s ease;
  }

  .character-delete-dialog-enter-active .delete-dialog-card,
  .character-delete-dialog-leave-active .delete-dialog-card {
    transition:
      opacity 0.18s ease,
      transform 0.18s ease;
  }

  .character-delete-dialog-enter-from,
  .character-delete-dialog-leave-to {
    opacity: 0;
  }

  .character-delete-dialog-enter-from .delete-dialog-card,
  .character-delete-dialog-leave-to .delete-dialog-card {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }

  @media (max-width: 900px) {
    .character-page {
      padding: 22px 20px 28px;
    }

    .page-header {
      flex-direction: column;
    }

    .create-button {
      align-self: flex-start;
    }

    .characters-grid {
      --character-card-min-width: 200px;
      --character-card-max-width: 236px;

      display: grid;
      grid-template-columns: repeat(
        auto-fit,
        minmax(min(var(--character-card-min-width), 100%), 1fr)
      );
      gap: 18px;
    }

    .characters-grid > * {
      width: 100%;
      justify-self: start;
    }
  }

  @media (max-width: 767px) {
    .character-page {
      display: flex;
      flex-direction: column;
      overflow: hidden;
      padding: 16px 16px 0;
      background: #fbf9ff;
    }

    .page-header {
      display: block;
    }

    .header-copy {
      width: 100%;
    }

    .title-row {
      min-height: 14px;
      gap: 0;
    }

    .page-title {
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 14px;
      line-height: 14px;
    }

    .total-badge,
    .create-button {
      display: none;
    }

    .page-description {
      margin-top: 8px;
      color: #6e6a7c;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 12px;
      line-height: 17px;
    }

    .content-area {
      margin-top: 16px;
      min-height: 0;
      padding-bottom: 16px;
      overflow-x: hidden;
      overflow-y: auto;
      flex: 1 1 auto;
    }

    .characters-grid {
      --character-card-min-width: 138px;
      --character-card-max-width: 174px;

      width: 100%;
      gap: 16px 12px;
    }

    .characters-grid > * {
      max-width: var(--character-card-max-width);
    }

    .delete-dialog-card {
      width: min(380px, calc(100vw - 48px));
      min-height: 198px;
    }

    .delete-dialog-header,
    .delete-dialog-body,
    .delete-dialog-actions {
      width: 100%;
    }

    .delete-dialog-description {
      width: 100%;
    }
  }
</style>
